import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { WatchPartyRecordingState } from '../../types/auth';

/**
 * CORE-1383 — есть ли в готовой записи изображение.
 *
 * Сама комната (`GET /api/watch-parties/{id}`) этого не знает: флаг `hasVideo`
 * отдаёт `GET /api/watch-parties/{id}/recording` (бэкенд, 30.09). До него плеер
 * узнавал об аудиозаписи только по кадру 0 × 0 на `loadedmetadata` (CORE-1573)
 * — то есть после того, как уже нарисовал чёрный прямоугольник, а на нативе не
 * узнавал вовсе.
 *
 * Запрос уходит один раз на готовую запись; `null` — не замерено, не узнали
 * или запись не готова: плеер тогда ведёт себя как раньше.
 */
export function useRecordingHasVideo(
  roomId: number | null | undefined,
  state: WatchPartyRecordingState | null | undefined,
): boolean | null {
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);

  useEffect(() => {
    if (typeof roomId !== 'number' || state !== 'Ready') {
      setHasVideo(null);
      return;
    }
    let alive = true;
    api.getRoomRecordingStatus(roomId)
      .then((status) => {
        if (!alive) return;
        setHasVideo(typeof status.hasVideo === 'boolean' ? status.hasVideo : null);
      })
      // Не прочитали — остаёмся при «не знаем», как и без запроса.
      .catch(() => { if (alive) setHasVideo(null); });
    return () => { alive = false; };
  }, [roomId, state]);

  return hasVideo;
}
